import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { 
  Plus, 
  Minus, 
  ShoppingCart, 
  CreditCard, 
  DollarSign,
  Check,
  Clock,
  Table as TableIcon,
  Search,
  Package,
  Car,
  Users,
  Receipt,
  History,
  Banknote,
  Pencil
} from 'lucide-react'
import type { Product, Category, DiningTable, Order, PaymentSummary } from '@/types'
import { toastHelpers } from '@/lib/toast-helpers'
import { orderStatusLabel, orderTypeLabel, statusLabel } from '@/lib/labels'
import { formatCurrency } from '@/lib/utils'
import { CashRegisterPanel } from '@/components/counter/CashRegisterPanel'

interface CartItem {
  product: Product
  quantity: number
  special_instructions?: string
}

interface CreateOrderRequest {
  table_id?: string
  customer_name?: string
  order_type: 'dine_in' | 'takeout' | 'delivery'
  items: Array<{
    product_id: string
    quantity: number
    special_instructions?: string
  }>
  notes?: string
}

interface ProcessPaymentRequest {
  payment_method: 'cash' | 'credit_card' | 'debit_card' | 'digital_wallet'
  amount: number
  reference_number?: string
}

export function CounterInterface() {
  const [activeTab, setActiveTab] = useState<'create' | 'orders' | 'payment' | 'cash'>('create')
  const [orderType, setOrderType] = useState<'dine_in' | 'takeout' | 'delivery'>('dine_in')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedTable, setSelectedTable] = useState<DiningTable | null>(null)
  const [customerName, setCustomerName] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [orderNotes, setOrderNotes] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Payment states
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [selectedActiveOrder, setSelectedActiveOrder] = useState<Order | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'credit_card' | 'debit_card' | 'digital_wallet'>('cash')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null)
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null)
  
  const queryClient = useQueryClient()

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.getCategories().then(res => res.data)
  })

  // Fetch products
  const { data: products = [] } = useQuery({
    queryKey: ['products', selectedCategory],
    queryFn: () => {
      if (selectedCategory === 'all') {
        return apiClient.getProducts().then(res => res.data)
      } else {
        return apiClient.getProductsByCategory(selectedCategory).then(res => res.data)
      }
    }
  })

  // Fetch available tables 
  const { data: tables = [] } = useQuery({
    queryKey: ['tables'],
    queryFn: () => apiClient.getTables().then(res => res.data)
  })

  // In-progress orders (kitchen pipeline)
  const { data: activeOrders = [] } = useQuery({
    queryKey: ['activeOrders'],
    queryFn: () => apiClient.getOrders({ status: 'pending,confirmed,preparing,ready', per_page: 50 }).then(res => res.data ?? []),
    refetchInterval: 5000,
  })

  // Fetch pending orders for payment processing
  const { data: pendingOrders = [] } = useQuery({
    queryKey: ['pendingOrders'],
    queryFn: () => apiClient.getOrders({ status: 'ready,served', per_page: 50 }).then(res => res.data ?? [])
  })

  // Create order mutation (counter endpoint - all order types)
  const createOrderMutation = useMutation({
    mutationFn: (orderData: CreateOrderRequest) => 
      apiClient.createOrderForRole(orderData),
    onSuccess: (response) => {
      setCart([])
      setSelectedTable(null)
      setCustomerName('')
      setOrderNotes('')
      setEditingOrderId(null)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['activeOrders'] })
      queryClient.invalidateQueries({ queryKey: ['newEnhancedKitchenOrders'] })
      queryClient.invalidateQueries({ queryKey: ['kitchenOrders'] })
      queryClient.invalidateQueries({ queryKey: ['tables'] })
      queryClient.invalidateQueries({ queryKey: ['cashShift'] })
      if (response.data) {
        setSelectedActiveOrder(response.data)
        setActiveTab('orders')
      }
    }
  })

  const updateOrderMutation = useMutation({
    mutationFn: ({ orderId, items, notes }: { orderId: string, items: CreateOrderRequest['items'], notes?: string }) =>
      apiClient.updateOrderItems(orderId, items, notes),
    onSuccess: () => {
      setCart([])
      setEditingOrderId(null)
      setOrderNotes('')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['activeOrders'] })
      queryClient.invalidateQueries({ queryKey: ['newEnhancedKitchenOrders'] })
      queryClient.invalidateQueries({ queryKey: ['pendingOrders'] })
      queryClient.invalidateQueries({ queryKey: ['cashShift'] })
      toastHelpers.success('Pedido actualizado', 'La cocina verá los cambios')
      setActiveTab('orders')
    },
    onError: (e: Error) => toastHelpers.error('Error al actualizar', e.message),
  })

  // Process payment mutation
  const processPaymentMutation = useMutation({
    mutationFn: ({ orderId, paymentData }: { orderId: string, paymentData: ProcessPaymentRequest, change?: number }) => 
      apiClient.processPaymentForRole(orderId, paymentData),
    onSuccess: (_data, variables) => {
      setSelectedOrder(null)
      setPaymentSummary(null)
      setPaymentAmount('')
      setReferenceNumber('')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['pendingOrders'] })
      queryClient.invalidateQueries({ queryKey: ['cashShift'] })
      if (variables.change && variables.change > 0) {
        toastHelpers.success('Pago procesado', `Entregar cambio: ${formatCurrency(variables.change)}`)
      } else {
        toastHelpers.success('Pago procesado', 'Pago del pedido completado')
      }
    },
    onError: (error: Error) => {
      toastHelpers.error('Error en el pago', error.message)
    }
  })

  // Filter products based on search
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  )

  // Available tables (for dine-in)
  const availableTables = tables.filter(table => !table.is_occupied)

  const addToCart = (product: Product) => {
    const existingItem = cart.find(item => item.product.id === product.id)
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ))
    } else {
      setCart([...cart, { product, quantity: 1 }])
    }
  }

  const removeFromCart = (productId: string) => {
    const existingItem = cart.find(item => item.product.id === productId)
    
    if (existingItem && existingItem.quantity > 1) {
      setCart(cart.map(item =>
        item.product.id === productId
          ? { ...item, quantity: item.quantity - 1 }
          : item
      ))
    } else {
      setCart(cart.filter(item => item.product.id !== productId))
    }
  }

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.product.price * item.quantity), 0)
  }

  const handleCreateOrder = () => {
    if (cart.length === 0) return
    if (orderType === 'dine_in' && !selectedTable && !editingOrderId) return

    const items = cart.map(item => ({
      product_id: item.product.id,
      quantity: item.quantity,
      special_instructions: item.special_instructions
    }))

    if (editingOrderId) {
      updateOrderMutation.mutate({ orderId: editingOrderId, items, notes: orderNotes || undefined })
      return
    }

    const orderData: CreateOrderRequest = {
      table_id: orderType === 'dine_in' ? selectedTable?.id : undefined,
      customer_name: customerName || undefined,
      order_type: orderType,
      items,
      notes: orderNotes || undefined
    }

    createOrderMutation.mutate(orderData)
  }

  const startEditOrder = (order: Order) => {
    if (!['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(order.status)) {
      toastHelpers.error('No se puede editar', 'Este pedido no se puede modificar')
      return
    }
    setEditingOrderId(order.id)
    setOrderType(order.order_type as 'dine_in' | 'takeout' | 'delivery')
    setCustomerName(order.customer_name || '')
    setOrderNotes(order.notes || '')
    if (order.table_id && tables.length) {
      const table = tables.find(t => t.id === order.table_id)
      if (table) setSelectedTable(table)
    }
    setCart((order.items || []).map(item => ({
      product: item.product || { id: item.product_id, name: 'Artículo', price: item.unit_price, is_available: true, preparation_time: 0, sort_order: 0, created_at: '', updated_at: '' } as Product,
      quantity: item.quantity,
      special_instructions: item.special_instructions,
    })))
    setActiveTab('create')
  }

  const cancelEdit = () => {
    setEditingOrderId(null)
    setCart([])
    setOrderNotes('')
  }

  const selectOrderForPayment = async (order: Order) => {
    setSelectedOrder(order)
    setPaymentSummary(null)
    setPaymentAmount('')
    try {
      const res = await apiClient.getPaymentSummary(order.id)
      if (res.data) {
        setPaymentSummary(res.data)
        setPaymentAmount(res.data.remaining_amount.toFixed(2))
      } else {
        setPaymentAmount(order.total_amount.toFixed(2))
      }
    } catch {
      setPaymentAmount(order.total_amount.toFixed(2))
    }
  }

  const handleProcessPayment = () => {
    if (!selectedOrder || !paymentAmount) return

    const tendered = Math.round(parseFloat(paymentAmount) * 100) / 100
    const due = Math.round((paymentSummary?.remaining_amount ?? selectedOrder.total_amount) * 100) / 100

    if (isNaN(tendered) || tendered <= 0) {
      toastHelpers.error('Monto inválido', 'Ingresa un monto mayor a cero')
      return
    }

    let amountToApply = tendered
    let change = 0

    if (paymentMethod === 'cash') {
      // ponytail: API records revenue applied to order, not bills in drawer; change is UI-only
      if (tendered >= due) {
        amountToApply = due
        change = Math.round((tendered - due) * 100) / 100
      }
    } else if (tendered > due + 0.001) {
      toastHelpers.error('Monto inválido', `Los pagos con tarjeta/digital no pueden exceder ${formatCurrency(due)}`)
      return
    }

    const paymentData: ProcessPaymentRequest = {
      payment_method: paymentMethod,
      amount: amountToApply,
      reference_number: referenceNumber || undefined
    }

    processPaymentMutation.mutate({ 
      orderId: selectedOrder.id, 
      paymentData,
      change,
    })
  }

  const paymentTendered = parseFloat(paymentAmount) || 0
  const amountDue = paymentSummary?.remaining_amount ?? selectedOrder?.total_amount ?? 0
  const changeDue = paymentMethod === 'cash'
    ? Math.max(0, Math.round((paymentTendered - amountDue) * 100) / 100)
    : 0

  const getOrderTypeIcon = (type: string) => {
    switch (type) {
      case 'dine_in': return <Users className="w-4 h-4" />
      case 'takeout': return <Package className="w-4 h-4" />
      case 'delivery': return <Car className="w-4 h-4" />
      default: return <ShoppingCart className="w-4 h-4" />
    }
  }

  const getOrderTypeBadge = (type: string) => {
    const configs = {
      dine_in: { label: orderTypeLabel.dine_in, color: 'bg-blue-100 text-blue-800' },
      takeout: { label: orderTypeLabel.takeaway, color: 'bg-green-100 text-green-800' },
      delivery: { label: orderTypeLabel.delivery, color: 'bg-purple-100 text-purple-800' }
    }
    const config = configs[type as keyof typeof configs] || configs.dine_in
    return <Badge className={config.color}>{config.label}</Badge>
  }

  const renderOrderItems = (order: Order) => (
    <div className="space-y-2 mt-3">
      {(order.items || []).map(item => (
        <div key={item.id} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
          <span>{item.quantity}x {item.product?.name || 'Artículo'}</span>
          <span className="font-medium">{formatCurrency(item.total_price)}</span>
        </div>
      ))}
      {(!order.items || order.items.length === 0) && (
        <p className="text-sm text-muted-foreground">No se cargaron artículos</p>
      )}
    </div>
  )

  const renderOrderCard = (order: Order, selected: boolean, onSelect: () => void) => (
    <Card
      key={order.id}
      className={`cursor-pointer transition-all ${selected ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getOrderTypeIcon(order.order_type)}
            <div>
              <div className="font-semibold">Pedido #{order.order_number}</div>
              <div className="text-sm text-muted-foreground">
                {order.customer_name && `${order.customer_name} • `}
                {order.table?.table_number && `Mesa ${order.table.table_number} • `}
                {(() => {
                  const n = order.items?.length || 0
                  return `${n} artículo${n !== 1 ? 's' : ''}`
                })()}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold">{formatCurrency(order.total_amount)}</div>
            <div className="flex items-center gap-2 justify-end">
              {getOrderTypeBadge(order.order_type)}
              <Badge variant="outline" className="text-xs">{statusLabel(order.status, orderStatusLabel)}</Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="flex h-screen bg-background">
      {/* Left Sidebar - Products and Orders */}
      <div className="w-2/3 border-r border-border overflow-hidden flex flex-col">
        {/* Header with Tabs */}
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Mostrador / Cobro</h1>
              <p className="text-muted-foreground">Crear pedidos y procesar pagos</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeTab === 'create' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('create')}
              >
                <Plus className="w-4 h-4 mr-1" />
                Crear pedido
              </Button>
              <Button
                variant={activeTab === 'orders' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('orders')}
              >
                <Clock className="w-4 h-4 mr-1" />
                Pedidos activos ({activeOrders.length})
              </Button>
              <Button
                variant={activeTab === 'payment' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('payment')}
              >
                <CreditCard className="w-4 h-4 mr-1" />
                Pago
              </Button>
              <Button
                variant={activeTab === 'cash' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('cash')}
              >
                <Banknote className="w-4 h-4 mr-1" />
                Caja
              </Button>
            </div>
          </div>

          {activeTab === 'create' && (
            <>
              {/* Order Type Selection */}
              <div className="flex gap-2 mb-4">
                <Button
                  variant={orderType === 'dine_in' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOrderType('dine_in')}
                >
                  <Users className="w-4 h-4 mr-1" />
                  {orderTypeLabel.dine_in}
                </Button>
                <Button
                  variant={orderType === 'takeout' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOrderType('takeout')}
                >
                  <Package className="w-4 h-4 mr-1" />
                  {orderTypeLabel.takeaway}
                </Button>
                <Button
                  variant={orderType === 'delivery' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOrderType('delivery')}
                >
                  <Car className="w-4 h-4 mr-1" />
                  {orderTypeLabel.delivery}
                </Button>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 overflow-x-auto">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                >
                  Todos
                </Button>
                {categories.map(category => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'create' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map(product => {
                const cartItem = cart.find(item => item.product.id === product.id)
                return (
                  <Card key={product.id} className="hover:shadow-md transition-shadow overflow-hidden">
                    <div className="aspect-video bg-muted overflow-hidden">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-muted-foreground">
                          {product.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg leading-tight">{product.name}</CardTitle>
                          {product.description && (
                            <CardDescription className="text-sm mt-1">
                              {product.description.substring(0, 60)}
                              {product.description.length > 60 ? '...' : ''}
                            </CardDescription>
                          )}
                        </div>
                        <div className="text-lg font-bold text-primary">
                          {formatCurrency(product.price)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {product.preparation_time > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {product.preparation_time} min
                            </Badge>
                          )}
                          {!product.is_available && (
                            <Badge variant="secondary" className="text-xs">
                              No disponible
                            </Badge>
                          )}
                        </div>

                        {product.is_available && (
                          <div className="flex items-center gap-2">
                            {cartItem ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeFromCart(product.id)}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <span className="w-8 text-center font-medium">
                                  {cartItem.quantity}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addToCart(product)}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => addToCart(product)}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Agregar
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          ) : activeTab === 'orders' ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Pedidos en cocina / En curso</h3>
              {activeOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay pedidos activos</p>
                  <p className="text-sm">Crea un pedido para enviarlo a cocina</p>
                </div>
              ) : (
                activeOrders.map(order => (
                  <div key={order.id} className="space-y-2">
                    {renderOrderCard(order, selectedActiveOrder?.id === order.id, () => setSelectedActiveOrder(order))}
                    {['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(order.status) && (
                      <Button variant="outline" size="sm" className="w-full" onClick={() => startEditOrder(order)}>
                        <Pencil className="w-4 h-4 mr-1" /> Editar pedido
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : activeTab === 'cash' ? (
            <CashRegisterPanel />
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Pedidos listos para cobrar</h3>
              {pendingOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay pedidos listos para cobrar</p>
                </div>
              ) : (
                pendingOrders.map(order =>
                  renderOrderCard(order, selectedOrder?.id === order.id, () => selectOrderForPayment(order))
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar */}
      {activeTab !== 'cash' && (
      <div className="w-1/3 flex flex-col bg-card">
        {activeTab === 'create' ? (
          /* Create Order Interface */
          <>
            {/* Table/Customer Selection */}
            <div className="p-4 border-b border-border">
              {editingOrderId && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded flex justify-between items-center">
                  <span className="text-sm font-medium text-amber-800">Editando pedido</span>
                  <Button variant="ghost" size="sm" onClick={cancelEdit}>Cancelar</Button>
                </div>
              )}
              {orderType === 'dine_in' ? (
                <>
                  <h3 className="font-semibold mb-3 flex items-center">
                    <TableIcon className="w-4 h-4 mr-2" />
                    Seleccionar mesa
                  </h3>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {availableTables.slice(0, 9).map(table => (
                      <Button
                        key={table.id}
                        variant={selectedTable?.id === table.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedTable(table)}
                        className="h-12"
                      >
                        {table.table_number}
                        <span className="text-xs block">
                          {table.seating_capacity} lugares
                        </span>
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Información del cliente</h3>
                </div>
              )}
              
              <Input
                placeholder={orderType === 'dine_in' ? 'Nombre del cliente (opcional)' : 'Nombre del cliente'}
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>

            {/* Cart */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <h3 className="font-semibold mb-3 flex items-center">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Artículos del pedido ({cart.length})
                </h3>
                
                {cart.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay artículos en el pedido</p>
                    <p className="text-sm">Agrega artículos del menú para comenzar</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cart.map(item => (
                      <div key={item.product.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.product.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatCurrency(item.product.price)} × {item.quantity}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <div className="font-medium">
                            {formatCurrency(item.product.price * item.quantity)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFromCart(item.product.id)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addToCart(item.product)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Order Notes */}
                {cart.length > 0 && (
                  <div className="mt-4">
                    <label className="text-sm font-medium">Notas del pedido</label>
                    <Input
                      placeholder="Solicitudes especiales o notas..."
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary and Actions */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-border bg-card">
                <div className="space-y-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total:</span>
                    <span>{formatCurrency(getTotalAmount())}</span>
                  </div>
                  
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleCreateOrder}
                    disabled={
                      cart.length === 0 || 
                      (orderType === 'dine_in' && !selectedTable && !editingOrderId) ||
                      createOrderMutation.isPending ||
                      updateOrderMutation.isPending
                    }
                  >
                    {(createOrderMutation.isPending || updateOrderMutation.isPending) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {editingOrderId ? 'Guardando...' : 'Creando pedido...'}
                      </>
                    ) : editingOrderId ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Guardar cambios del pedido
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Crear pedido {orderType === 'dine_in' ? orderTypeLabel.dine_in : orderType === 'takeout' ? orderTypeLabel.takeaway : orderTypeLabel.delivery}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : activeTab === 'orders' ? (
          <>
            {selectedActiveOrder ? (
              <div className="flex-1 overflow-y-auto p-4">
                <h3 className="font-semibold mb-2">Pedido #{selectedActiveOrder.order_number}</h3>
                <div className="flex gap-2 mb-4">
                  {getOrderTypeBadge(selectedActiveOrder.order_type)}
                  <Badge variant="outline">{statusLabel(selectedActiveOrder.status, orderStatusLabel)}</Badge>
                </div>
                {selectedActiveOrder.customer_name && (
                  <p className="text-sm text-muted-foreground mb-2">Cliente: {selectedActiveOrder.customer_name}</p>
                )}
                <h4 className="font-medium mb-2">Artículos</h4>
                {renderOrderItems(selectedActiveOrder)}
                <div className="flex justify-between font-semibold text-lg mt-4 pt-4 border-t">
                  <span>Total</span>
                  <span>{formatCurrency(selectedActiveOrder.total_amount)}</span>
                </div>
                {['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(selectedActiveOrder.status) && (
                  <Button variant="outline" className="w-full mt-3" onClick={() => startEditOrder(selectedActiveOrder)}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar pedido
                  </Button>
                )}
                {selectedActiveOrder.status === 'ready' && (
                  <Button className="w-full mt-4" onClick={() => {
                    setSelectedOrder(selectedActiveOrder)
                    setPaymentAmount(selectedActiveOrder.total_amount.toString())
                    setActiveTab('payment')
                  }}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Ir al pago
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Selecciona un pedido para ver los artículos</p>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Payment Processing Interface */
          <>
            {selectedOrder ? (
              <>
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold mb-3">Detalles del pago</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Pedido:</span>
                      <span>#{selectedOrder.order_number}</span>
                    </div>
                    {selectedOrder.customer_name && (
                      <div className="flex justify-between">
                        <span>Cliente:</span>
                        <span>{selectedOrder.customer_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <span>{formatCurrency(paymentSummary?.total_amount ?? selectedOrder.total_amount)}</span>
                    </div>
                    {paymentSummary && paymentSummary.total_paid > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Pagado:</span>
                        <span>{formatCurrency(paymentSummary.total_paid)}</span>
                      </div>
                    )}
                    {paymentSummary && (
                      <div className="flex justify-between font-semibold text-primary">
                        <span>Restante:</span>
                        <span>{formatCurrency(paymentSummary.remaining_amount)}</span>
                      </div>
                    )}
                  </div>
                  <h4 className="font-medium px-4 pt-2">Artículos</h4>
                  <div className="px-4 pb-2">{renderOrderItems(selectedOrder)}</div>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Método de pago</label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentMethod('cash')}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Efectivo
                      </Button>
                      <Button
                        variant={paymentMethod === 'credit_card' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentMethod('credit_card')}
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Crédito
                      </Button>
                      <Button
                        variant={paymentMethod === 'debit_card' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentMethod('debit_card')}
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Débito
                      </Button>
                      <Button
                        variant={paymentMethod === 'digital_wallet' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentMethod('digital_wallet')}
                      >
                        Digital
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      {paymentMethod === 'cash' ? 'Efectivo recibido' : 'Monto del pago'}
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                    />
                    {paymentMethod === 'cash' && changeDue > 0 && (
                      <p className="text-sm font-medium text-green-600 mt-2">
                        Cambio a entregar: {formatCurrency(changeDue)}
                      </p>
                    )}
                    {paymentMethod === 'cash' && paymentTendered > 0 && paymentTendered < amountDue && (
                      <p className="text-sm text-amber-600 mt-2">
                        Pago parcial — aún faltan {formatCurrency(amountDue - paymentTendered)}
                      </p>
                    )}
                  </div>

                  {paymentMethod !== 'cash' && (
                    <div>
                      <label className="text-sm font-medium mb-1 block">Número de referencia</label>
                      <Input
                        placeholder="Referencia de transacción"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                      />
                    </div>
                  )}

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleProcessPayment}
                    disabled={!paymentAmount || processPaymentMutation.isPending}
                  >
                    {processPaymentMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Procesar pago
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Selecciona un pedido para procesar el pago</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  )
}
